import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useSiteBranding from '../hooks/useSiteBranding';
import TrustSupportBanner from '../components/TrustSupportBanner';
import './TrustPages.css';

const PAGE_TITLE = 'Payment Methods';

const PAYMENT_OPTIONS = [
  {
    id: 'jazzcash',
    name: 'JazzCash',
    iconClass: 'payment-method-card__icon--jazzcash',
    badge: 'Mobile wallet',
    description:
      'Pay instantly from your JazzCash app. Send the order amount to our JazzCash account and share the transaction ID at checkout.',
    steps: ['Open JazzCash → Send Money', 'Enter our account number shown at checkout', 'Complete payment and upload proof if asked'],
  },
  {
    id: 'easypaisa',
    name: 'Easypaisa',
    iconClass: 'payment-method-card__icon--easypaisa',
    badge: 'Mobile wallet',
    description:
      'Use your Easypaisa wallet for a quick transfer. Account details appear on the checkout page when you select this method.',
    steps: ['Open Easypaisa → Send Money', 'Use the account number from checkout', 'Keep your receipt for order confirmation'],
  },
  {
    id: 'bank',
    name: 'Bank Transfer',
    iconClass: 'payment-method-card__icon--bank',
    badge: 'Bank account',
    description:
      'Transfer directly from any Pakistani bank. We share account title, bank name, and account number during checkout.',
    steps: ['Choose Bank Transfer at checkout', 'Transfer the exact order total', 'Submit proof or reference on the order page'],
  },
  {
    id: 'qr',
    name: 'QR Code',
    iconClass: 'payment-method-card__icon--qr',
    badge: 'Scan & pay',
    description:
      'Scan our payment QR code with your banking or wallet app for a fast, error-free transfer.',
    steps: ['Select QR payment at checkout', 'Scan the QR with JazzCash, Easypaisa, or bank app', 'Confirm payment and complete your order'],
  },
  {
    id: 'cod',
    name: 'Cash on Delivery (COD)',
    iconClass: 'payment-method-card__icon--cod',
    badge: 'Pay on delivery',
    description:
      'Pay in cash when your order arrives at your door. Available on eligible orders across Pakistan.',
    steps: ['Choose COD at checkout', 'Verify your delivery address and phone', 'Pay the courier when you receive your parcel'],
  },
];

export default function PaymentMethodsPage() {
  const { siteName } = useSiteBranding();
  const brand = siteName || 'Super Wear';

  useEffect(() => {
    document.title = `${PAGE_TITLE} — ${brand}`;
  }, [brand]);

  return (
    <div className="trust-page">
      <div className="trust-page__inner trust-page__inner--wide">
        <header className="trust-page__hero">
          <p className="trust-page__hero-eyebrow">Secure checkout</p>
          <h1 className="trust-page__title">{PAGE_TITLE}</h1>
          <p className="trust-page__subtitle">
            {brand} accepts trusted payment options across Pakistan — mobile wallets, bank transfer, QR scan, and cash on
            delivery. Choose what works best for you at checkout.
          </p>
        </header>

        <TrustSupportBanner variant="inline" />

        <div className="payment-methods-grid" role="list">
          {PAYMENT_OPTIONS.map((method) => (
            <article key={method.id} className="payment-method-card" role="listitem">
              <div className="payment-method-card__head">
                <div className={`payment-method-card__icon ${method.iconClass}`} aria-hidden>
                  {method.id === 'jazzcash' && 'J'}
                  {method.id === 'easypaisa' && 'E'}
                  {method.id === 'bank' && '🏦'}
                  {method.id === 'qr' && '▦'}
                  {method.id === 'cod' && '💵'}
                </div>
                <div>
                  <h2 className="payment-method-card__title">{method.name}</h2>
                  <span className="payment-method-card__badge">{method.badge}</span>
                </div>
              </div>
              <p className="payment-method-card__desc">{method.description}</p>
              <ol className="payment-method-card__steps">
                {method.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </article>
          ))}
        </div>

        <section className="trust-section payment-methods-note">
          <h2>How to pay</h2>
          <p>
            Add items to your bag, go to <strong>Checkout</strong>, and pick your preferred method. For JazzCash,
            Easypaisa, bank, and QR, account numbers or QR codes are shown on the checkout page after you select a
            payment option.
          </p>
          <p>
            Need help? <Link to="/contact">Contact us</Link> on phone, email, or WhatsApp — support is open{' '}
            <strong>24/7</strong> and we&apos;re happy to guide you through payment.
          </p>
          <div className="payment-methods-cta">
            <Link to="/bag" className="payment-methods-cta__btn payment-methods-cta__btn--primary">
              View bag &amp; checkout
            </Link>
            <Link to="/contact" className="payment-methods-cta__btn payment-methods-cta__btn--secondary">
              Contact support
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
